import { FilterController, FilterList, FilterPanel, FilterSelections } from "@amzn/stencil-react-components/filtering";
import { View } from "@amzn/stencil-react-components/layout";
import { trackCustomEvent } from "@amzn/uxm-stencil-react-components/lib/src/uxm/custom-event";
import { config } from "config";
import { FilterProps } from "hooks";
import React, { FC } from "react";

import { CategoryFilterList } from "./CategoryFilterList";

// TODO: Move options to file
export enum SearchFilter {
  CATEGORY = "category",
  OS = "os",
  COST_CONDITION = "costCondition",
}

export enum CostOptions {
  ChargeToCostCenter = "chargeToCostCenter",
  Free = "free",
}

export enum OSOptions {
  Windows = "Windows",
  Mac = "Mac",
  Linux = "Linux",
}

export const categoryList = [
  "Accessibility Tools",
  "BI & Data Visualisation",
  "Communication",
  "Database and Data Analysis Tools",
  "Development and Programming Tools",
  "File Collaboration & Sharing",
  "Learning & Knowledge Mgmt",
  "Multimedia",
  "Office tools",
  "Presentation",
  "Project & Task Mgmt",
  "Security and Network Tools",
  "Survey",
  "Utility and System Tools",
  "Visual Design",
  "Whiteboarding",
  "Others",
];

const categoryOptions = categoryList.map((category) => ({
  label: category,
  value: category,
}));

// Add AI For Me as a special category option
const aiForMeOption = {
  label: "AI",
  value: "aiforme-link",
};

const allCategoryOptions = [aiForMeOption, ...categoryOptions];

const DO_NOT_TRUNCATE_CATEGORY_LIST_THRESHOLD = 1000;

export interface SearchFilterPanelProps {
  filters: FilterProps;
  onApply: (e: any) => void;
}

export const SearchFilterPanel: FC<SearchFilterPanelProps> = (props) => {
  return (
    <View height={"calc(100% - 65px)"} className="search-results-filter-column">
      <FilterPanel
        isInFlyout
        onApply={(e: any) => {
          trackUxmFilterPanelApply(e);
          props.onApply(e);
        }}
        titleText="Filters"
      >
        {renderOSCompatibilityFilter(props.filters.os)}
        {/* {renderUsedByFilter()} */}
        {/* {renderDataClassificationFilter()} */}
        {renderCostConditionFilter(props.filters.costCondition)}
        {renderCategoryFilter(props.filters.category)}
        {/* {renderExternalSharingFilter()} */}
      </FilterPanel>
    </View>
  );
};

const trackUxmFilterPanelApply = (selections?: FilterSelections) => {
  const trackerId = config.analytics.uxmAppId;
  const filtersListPayload = {
    selections: selections,
  };

  trackCustomEvent(filtersListPayload, "search-filter-panel", trackerId);
};

function renderExternalSharingFilter() {
  return (
    <FilterController
      dataTestId="search-filter-external-sharing"
      isDefaultExpanded={false}
      name="externalSharing"
      titleText="External sharing"
    >
      <FilterList
        titleText="External sharing"
        resetButtonText={"Clear"}
        isMulti={true}
        options={[
          {
            label: "External sharing",
            value: "external sharing",
          },
          {
            label: "Internal sharing",
            value: "internal sharing",
          },
        ]}
        value={undefined}
      />
    </FilterController>
  );
}

function renderCostConditionFilter(value: string[] | undefined) {
  return (
    <FilterController
      dataTestId="search-filter-cost-condition"
      isDefaultExpanded={false}
      name={SearchFilter.COST_CONDITION}
      titleText="Cost condition"
    >
      <FilterList
        titleText="Cost condition"
        resetButtonText={"Clear"}
        isMulti={true}
        options={[
          {
            label: "Free",
            value: CostOptions.Free,
          },
          {
            label: "Charge to cost center",
            value: CostOptions.ChargeToCostCenter,
          },
        ]}
        value={value}
      />
    </FilterController>
  );
}

function renderDataClassificationFilter() {
  return (
    <FilterController
      dataTestId="search-filter-data-classification"
      isDefaultExpanded={false}
      name="dataClassification"
      titleText="Data classification"
    >
      <FilterList
        titleText="Data classification"
        resetButtonText={"Clear"}
        isMulti={true}
        options={[
          {
            label: "High confidential data",
            value: "high confidential data",
          },
          {
            label: "Medium confidential data",
            value: "medium confidential data",
          },
          {
            label: "Low confidential data",
            value: "low confidential data",
          },
        ]}
        value={undefined}
      />
    </FilterController>
  );
}

function renderUsedByFilter() {
  return (
    <FilterController
      dataTestId="search-filter-used-by"
      isDefaultExpanded={false}
      name="usedBy"
      titleText="Used by team/org"
    >
      <FilterList
        titleText="Used by team/org"
        resetButtonText={"Clear"}
        isMulti={true}
        options={[
          {
            label: "Used by my team",
            value: "used by my team",
          },
          {
            label: "Used by my org",
            value: "used by my org",
          },
        ]}
        value={undefined}
      />
    </FilterController>
  );
}

function renderOSCompatibilityFilter(value: string[] | undefined) {
  return (
    <FilterController
      dataTestId="search-filter-os-compatibility"
      isDefaultExpanded={false}
      name={SearchFilter.OS}
      titleText="OS compatibility"
    >
      <FilterList
        titleText="OS compatibility"
        resetButtonText={"Clear"}
        isMulti={true}
        options={[
          {
            label: "MacOS",
            value: OSOptions.Mac,
          },
          {
            label: "WindowOS",
            value: OSOptions.Windows,
          },
          {
            label: "Linux",
            value: OSOptions.Linux,
          },
        ]}
        value={value}
      />
    </FilterController>
  );
}

function renderCategoryFilter(value: string[] | undefined) {
  return (
    <FilterController
      dataTestId="search-filter-category"
      isDefaultExpanded={false}
      name={SearchFilter.CATEGORY}
      titleText="Category"
      showMoreThreshold={DO_NOT_TRUNCATE_CATEGORY_LIST_THRESHOLD}
    >
      <CategoryFilterList
        titleText="Category"
        resetButtonText={"Clear"}
        isMulti={true}
        options={allCategoryOptions}
        value={value}
      />
    </FilterController>
  );
}
